import type React from 'react';

import { useGetDepartment, usePatchDepartment } from '@vlabs/api/artemy/client';
import { CrossCircleIcon } from '@vlabs/icons';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useDetachDepartmentListTranslation } from './i18n';
import type { DetachDepartmentListProps } from './types';

type DetachDepartmentListIconButtonProps = DetachDepartmentListProps & Omit<IconButtonProps, 'onClick'>;

const displayName = 'DetachDepartmentListIconButton';
const testid = buildTestid(displayName);

function DetachDepartmentListIconButton({
  departmentId,
  listIds,
  'data-testid': dataTestId = undefined,
  ...props
}: DetachDepartmentListIconButtonProps): React.JSX.Element {
  const { t } = useDetachDepartmentListTranslation();
  const getDepartment = useGetDepartment(departmentId);
  const patchDepartment = usePatchDepartment({
    mutation: {
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });
  const isDisabled = getDepartment.isLoading
    || getDepartment.isError
    || !getDepartment.data
    || patchDepartment.isPending;

  const handleDetach = (): void => {
    const filteredLists = (getDepartment.data?.lists ?? []).filter(
      ({ list_id }) => !listIds.includes(list_id),
    );

    patchDepartment.mutate({
      departmentId,
      data: { lists: filteredLists },
    });
  };

  return (
    <IconButton
      disabled={props.disabled ?? isDisabled}
      icon={<CrossCircleIcon />}
      kind="negative"
      onClick={handleDetach}
      title={t('title')}
      variant="dimmed"
      {...props}
      {...testid(dataTestId)}
    />
  );
}

DetachDepartmentListIconButton.displayName = displayName;

export { DetachDepartmentListIconButton };
